import { CommonModule } from '@angular/common';
import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyDirective } from 'ngx-currency';
import { MaskService } from '../../services/mask.service';
import { ControleEstoqueService } from '../../services/apis/produto/controle-estoque.service';
import { ControleEstoqueDetalheLotesRequest } from '../../services/requests/produto/controle-estoque-detalhe-lotes.request';
import { AlertCardComponent } from '../alert-card/alert-card.component';

export interface LoteSaidaEstoqueModalData {
  idProduto: number;
  idLocalizacaoEstoque: number;
  nomeProduto: string;
  skuProduto?: string;
  quantidadeTotal: number;
  lotes: LoteSaidaEstoqueModalItem[];
}

export interface LoteSaidaEstoqueModalItem {
  idLote: number;
  codigoLote: string;
  dataValidade?: string;
  saldoDisponivel: number;
  quantidadeSaida: number;
}

@Component({
  selector: 'app-lote-saida-estoque-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatTableModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    NgxCurrencyDirective,
    AlertCardComponent,
  ],
  templateUrl: './lote-saida-estoque-modal.component.html',
  styleUrl: './lote-saida-estoque-modal.component.css',
})
export class LoteSaidaEstoqueModalComponent implements OnInit {
  public nomeProduto: string = '';
  public skuProduto: string = '';
  public quantidadeTotal: number = 0;
  public isLoading: boolean = false;
  public erroCarga: string | null = null;

  public dataSource = new MatTableDataSource<LoteSaidaEstoqueModalItem>([]);
  public displayedColumns: string[] = ['codigoLote', 'dataValidade', 'saldoDisponivel', 'quantidadeSaida'];

  public quantidadeMask = this.maskService.getDynamicDecimalMask();

  public isNarrow = window.innerWidth <= 700;
  @HostListener('window:resize') onResize(): void {
    this.isNarrow = window.innerWidth <= 700;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: LoteSaidaEstoqueModalData,
    public dialogRef: MatDialogRef<LoteSaidaEstoqueModalComponent>,
    private controleEstoqueService: ControleEstoqueService,
    private maskService: MaskService
  ) {}

  ngOnInit(): void {
    this.nomeProduto = this.data.nomeProduto;
    this.skuProduto = this.data.skuProduto ?? '';
    this.quantidadeTotal = this.data.quantidadeTotal;
    this.carregarLotes();
  }

  private carregarLotes(): void {
    this.isLoading = true;
    this.erroCarga = null;

    const request: ControleEstoqueDetalheLotesRequest = {
      idLocalizacaoEstoque: this.data.idLocalizacaoEstoque,
      idSituacaoFiltro: 0,
    };

    this.controleEstoqueService.getLotes(this.data.idProduto, request, 0).subscribe({
      next: (resp) => {
        const disponiveis: LoteSaidaEstoqueModalItem[] = (resp?.list ?? [])
          .filter(l => (l.saldoDisponivel ?? 0) > 0)
          .map(l => ({
            idLote: l.idLote,
            codigoLote: l.numeroLote,
            dataValidade: l.dataValidade,
            saldoDisponivel: l.saldoDisponivel,
            quantidadeSaida: 0,
          }));

        const savedMap = new Map<number, LoteSaidaEstoqueModalItem>();
        for (const s of this.data.lotes ?? []) {
          savedMap.set(s.idLote, s);
        }
        for (const linha of disponiveis) {
          if (savedMap.has(linha.idLote)) {
            linha.quantidadeSaida = savedMap.get(linha.idLote)!.quantidadeSaida;
          }
        }

        this.dataSource.data = disponiveis;
        this.isLoading = false;
      },
      error: () => {
        this.erroCarga = 'Não foi possível carregar lotes disponíveis.';
        this.dataSource.data = this.data.lotes ?? [];
        this.isLoading = false;
      },
    });
  }

  public get totalDistribuido(): number {
    return this.dataSource.data.reduce((sum, l) => sum + (l.quantidadeSaida || 0), 0);
  }

  public get totalOk(): boolean {
    return Math.abs(this.totalDistribuido - this.quantidadeTotal) < 0.0001;
  }

  public get statusBadge(): 'vazio' | 'parcial' | 'ok' {
    if (this.totalDistribuido === 0) return 'vazio';
    return this.totalOk ? 'ok' : 'parcial';
  }

  public isVencido(dataValidade: string): boolean {
    return new Date(dataValidade) < new Date();
  }

  public isLinhaExcedida(item: LoteSaidaEstoqueModalItem): boolean {
    return (item.quantidadeSaida || 0) > (item.saldoDisponivel || 0);
  }

  public get temLinhaExcedida(): boolean {
    return this.dataSource.data.some(l => this.isLinhaExcedida(l));
  }

  public confirmar(): void {
    if (this.temLinhaExcedida) return;
    this.dialogRef.close({ confirmed: true, lotes: this.dataSource.data.filter(l => (l.quantidadeSaida || 0) > 0) });
  }

  public cancelar(): void {
    this.dialogRef.close({ confirmed: false });
  }
}
